import { useEffect, useId, useRef, useState } from "react";
import { clampInt, parseWholeNumber } from "./lobbyLogic";

const DEBOUNCE_MS = 600;

/**
 * Whole-number field with -/+ buttons. Typed values are sent after a short pause (debounced) and only when
 * they are inside the allowed range. On blur the text snaps to the nearest valid value.
 */
export function NumberField({
  label,
  value,
  min,
  max,
  step,
  prefix,
  suffix,
  errorText,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  prefix?: string;
  suffix?: string;
  errorText: string;
  onCommit: (value: number) => void;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [text, setText] = useState(String(value));

  // Follow the server value unless the player is typing.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setText(String(value));
  }, [value]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const parsed = parseWholeNumber(text);
  const invalid = parsed === null || parsed < min || parsed > max;

  const commit = (next: number) => {
    if (next !== value) onCommit(next);
  };

  const onType = (raw: string) => {
    setText(raw);
    if (timer.current) clearTimeout(timer.current);
    const n = parseWholeNumber(raw);
    if (n === null || n < min || n > max) return;
    timer.current = setTimeout(() => commit(n), DEBOUNCE_MS);
  };

  const onBlur = () => {
    if (timer.current) clearTimeout(timer.current);
    const n = parseWholeNumber(text);
    const next = n === null ? value : clampInt(n, min, max);
    setText(String(next));
    commit(next);
  };

  const bump = (direction: 1 | -1) => {
    if (timer.current) clearTimeout(timer.current);
    const base = parsed ?? value;
    // Step to the next multiple of `step` in the chosen direction.
    const snapped = direction === 1 ? (Math.floor(base / step) + 1) * step : (Math.ceil(base / step) - 1) * step;
    const next = clampInt(snapped, min, max);
    setText(String(next));
    commit(next);
  };

  const errorId = `${id}-error`;
  const stepButton =
    "grid size-11 shrink-0 place-items-center text-lg text-ink hover:bg-surface-2 disabled:opacity-40 disabled:pointer-events-none";
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-ink">
        {label}
      </label>
      <div className="flex w-full max-w-[15rem] items-center overflow-hidden rounded-lg border border-line bg-surface focus-within:outline focus-within:outline-[3px] focus-within:outline-offset-2 focus-within:outline-[color:var(--focus)]">
        <button type="button" className={stepButton} aria-label={`${label} verringern`} onClick={() => bump(-1)} disabled={(parsed ?? value) <= min}>
          <span aria-hidden="true">−</span>
        </button>
        <div className="flex min-w-0 flex-1 items-center justify-center gap-1 border-x border-line">
          {prefix && <span className="text-ink-2" aria-hidden="true">{prefix}</span>}
          <input
            ref={inputRef}
            id={id}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={text}
            aria-invalid={invalid}
            aria-describedby={invalid ? errorId : undefined}
            onChange={(e) => onType(e.target.value)}
            onBlur={onBlur}
            className="h-11 w-full min-w-0 bg-transparent text-center font-semibold tabular-nums text-ink outline-none"
          />
          {suffix && <span className="pr-2 text-ink-2" aria-hidden="true">{suffix}</span>}
        </div>
        <button type="button" className={stepButton} aria-label={`${label} erhöhen`} onClick={() => bump(1)} disabled={(parsed ?? value) >= max}>
          <span aria-hidden="true">+</span>
        </button>
      </div>
      {invalid && (
        <p id={errorId} role="alert" className="mt-1.5 text-sm text-danger">
          {errorText}
        </p>
      )}
    </div>
  );
}
